<script setup lang="ts">
import clone from 'lodash/clone'
import { onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import type {
  GoogleMapAutocompleteOptions,
  GoogleMapAutocompleteRestrictions,
  GooglePlace,
  GooglePlacesResponse,
} from '~/definitions/GoogleDefinitions'
import { useMainStore } from '~/store/MainStore'
import type { Address } from '~/definitions/AllDefinitions'

const emit = defineEmits(['place-changed'])
interface GooglePlacesAutocompleteProps {
  input?: string
  placeholder?: string
  types?: string
  country?: string
  clearAddressOnPlaceSelected?: boolean
}
const props = withDefaults(defineProps<GooglePlacesAutocompleteProps>(), {
  input: '',
  placeholder: 'Start typing...',
  types: 'address|establishment',
  country: 'us',
  clearAddressOnPlaceSelected: true,
})
const loaded = ref(false)
const { currentLocation } = storeToRefs(useMainStore())
const options = ref({
  input: '',
  componentRestrictions: {
    country: [],
  },
} as GoogleMapAutocompleteOptions)
function clearSearch() {
  if (props.clearAddressOnPlaceSelected) {
    const inputs = document.getElementsByClassName('g-map-autocomplete')
    if (inputs && inputs.length) {
      (inputs[0] as HTMLInputElement).value = ''
    }
  }
}
function getAddress(place: GooglePlace[] | null, geometry?: any) {
  if (!place) {
    return {}
  }
  const theAddress = {
    state: null,
    addressOne: '',
    addressTwo: null,
    addressThree: null,
    city: null,
    latitude: 0,
    longitude: 0,
    firstName: '',
    lastName: '',
    email: '',
    phoneFormatted: null,
    phoneUnformatted: null,
    phoneCountry: 'us',
    phoneType: 'unknown',
    postalCode: '',
  } as Address
  if (geometry && geometry.location) {
    theAddress.latitude = geometry.location.lat()
    theAddress.longitude = geometry.location.lng()
  }
  let neighborhood = ''
  for (let i = 0; i < place.length; i++) {
    const currentPlace = place[i]
    currentPlace.types.forEach((addressType) => {
      switch (addressType) {
        case 'neighborhood':
          neighborhood = currentPlace.long_name
          break
        case 'street_number':
        case 'route':
          if (theAddress.addressOne && theAddress.addressOne.length) {
            theAddress.addressOne += ' '
          }
          theAddress.addressOne += currentPlace.short_name
          break
        case 'locality':
          theAddress.city = currentPlace.long_name
          break
        case 'administrative_area_level_1':
          theAddress.state = currentPlace.short_name
          break
        case 'administrative_area_level_2':
          theAddress.country = currentPlace.short_name
          break
        case 'postal_code':
          theAddress.postalCode = currentPlace.long_name
          break
        case 'country':
          theAddress.country = currentPlace.short_name
          break
        case 'sublocality_level_1':
          theAddress.sublocality = currentPlace.long_name
          break
      }
    })
  }
  if (!theAddress.city && neighborhood.length) {
    theAddress.city = neighborhood
  }
  return theAddress
}
function handlePlaceChanged(data: GooglePlacesResponse | null) {
  if (data) {
    const tempAddress = getAddress(data.address_components, data.geometry) as any
    // tempAddress.formattedAddress = data.formatted_address
    emit('place-changed', tempAddress)
  } else {
    emit('place-changed', null)
  }
  clearSearch()
}
function initOptions() {
  loaded.value = false
  options.value.input = clone(props.input)
  if (typeof options.value.componentRestrictions === 'undefined') {
    options.value.componentRestrictions = {
      country: [],
    } as GoogleMapAutocompleteRestrictions
  }
  options.value.componentRestrictions.country = []
  if (currentLocation && currentLocation.value && currentLocation.value.country) {
    options.value.componentRestrictions.country.push(currentLocation.value.country.toLowerCase())
  } else {
    options.value.componentRestrictions.country.push(props.country.toLowerCase())
  }
  loaded.value = true
}
onMounted(() => {
  initOptions()
})
</script>

<template>
  <div class="w-full">
    <div
      v-if="loaded"
      class="w-full"
    >
      <GMapAutocomplete
        :options="options"
        :country="country"
        :placeholder="placeholder"
        class="simple-input w-full g-map-autocomplete"
        @place_changed="handlePlaceChanged"
      />
    </div>
  </div>
</template>

<style scoped>

</style>
